/**
 * TokenSavingsPanel — panel global de ahorro de tokens (Sprint 280 Stream H).
 *
 * REQ-CHAT-039 §2/§7: KPI principal de ahorro de coste efectivo % con
 * conmutador coste/tokens, gráfica con las series producido · enviado ·
 * facturable equivalente, desglose por dimensión y tokens cacheados (con
 * «no disponible» cuando el proveedor no los reporta). Solo agregados.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import apiClient from '@/lib/api-client';
import { useI18n } from '@/i18n';

export interface TokenSavingsView {
  window: string;
  unit: string;
  group_by: string;
  kpis: {
    savings_cost_pct: number | null;
    savings_tokens_pct: number | null;
    cache_hit_rate: number | null;
    cached_tokens: {
      read: number | null;
      write: number | null;
      pct: number | null;
      available: boolean;
    };
    output_weight_pct: number | null;
    coverage_pct: number | null;
    calls: number;
    holdout_calls: number;
    rate_missing_calls: number;
    breaking_calls: number;
    tokens: Record<string, number>;
    cost: Record<string, number>;
  };
  series: Array<{
    bucket: string;
    produced: number;
    sent: number;
    billable_equivalent: number;
  }>;
  breakdown: Array<{
    key: string;
    calls: number;
    tokens_saved_s2b: number;
    savings_cost_pct: number | null;
  }>;
  /** ISS-808: distingue «sin ahorro medido» de «ahorro = 0». */
  measurement?: {
    savings_state: 'measured' | 'no_measured_savings' | 'no_data';
    signals: string[];
    calls: number;
  };
}

/** Formatting helpers exported for unit tests (pure). */
export function formatPct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${value.toFixed(1)} %`;
}

export function formatCachedTokens(
  cached: TokenSavingsView['kpis']['cached_tokens'],
  notAvailableLabel: string,
): string {
  if (!cached?.available) return notAvailableLabel;
  const read = cached.read ?? 0;
  const write = cached.write ?? 0;
  return `${read.toLocaleString()} ↑ / ${write.toLocaleString()} ↓`;
}

/**
 * Valor del KPI de ahorro (ISS-808). Sin señal medida, `0 %` es ambiguo: se
 * muestra «—» y el aviso lo explica.
 */
export function savingsKpiValue(
  measurement: TokenSavingsView['measurement'] | undefined,
  kpis: TokenSavingsView['kpis'],
  unit: 'cost' | 'tokens',
): string {
  const state = measurement?.savings_state;
  if (state === 'no_measured_savings' || state === 'no_data') return '—';
  return formatPct(unit === 'cost' ? kpis.savings_cost_pct : kpis.savings_tokens_pct);
}

/** Endpoint del panel según ámbito (tenant o plataforma/DR). Pure — testeable. */
export function tokenSavingsEndpoint(input: {
  drCode?: string;
  tenantId?: string;
}): string | null {
  // Rutas ABSOLUTAS con /api/v1: el TenantAwareApiClient no las prefija
  // (NON_TENANT_ROUTES). Sin el prefijo, quedaban como
  // /api/v1/tenants/{current}/tenants/{tid}/... → 404 (fix 19-Oct).
  if (input.tenantId) return `/api/v1/tenants/${input.tenantId}/token-savings`;
  if (input.drCode) return `/api/v1/admin/dr/${input.drCode}/token-savings`;
  return null;
}

export function useTokenSavings(
  drCode: string | undefined,
  window: string,
  unit: string,
  groupBy: string,
  tenantId?: string,
) {
  const endpoint = tokenSavingsEndpoint({ drCode, tenantId });
  return useQuery({
    queryKey: ['token-savings', tenantId ?? drCode, window, unit, groupBy],
    enabled: Boolean(endpoint),
    retry: false,
    queryFn: async () => {
      const { data } = await apiClient.get<TokenSavingsView>(endpoint!, {
        params: { window, unit, group_by: groupBy },
      });
      return data;
    },
  });
}

export function TokenSavingsPanel({
  drCode,
  tenantId,
}: {
  drCode?: string;
  tenantId?: string;
}) {
  const { t } = useI18n();
  const [window, setWindow] = useState('7d');
  const [unit, setUnit] = useState<'cost' | 'tokens'>('cost');
  // Fix 19-Oct: el ámbito puede llegar async (contexto de tenant). El grupo
  // se sincroniza con el ámbito para no pedir `group_by=tenant` al endpoint
  // del tenant (422 → «sin datos»).
  const groupOptions = tenantId
    ? (['agent', 'hive', 'model', 'cost_center'] as const)
    : (['tenant', 'agent', 'hive', 'model', 'cost_center'] as const);
  const [groupBy, setGroupBy] = useState<string>(tenantId ? 'agent' : 'tenant');
  // Sin ámbito no hay panel que mostrar (19-Oct: nada de placeholders).
  useEffect(() => {
    if (tenantId && !groupOptions.includes(groupBy as never)) {
      setGroupBy('agent');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, groupBy]);

  const { data, isLoading, isError } = useTokenSavings(
    drCode,
    window,
    unit,
    groupBy,
    tenantId,
  );

  const series = useMemo(() => data?.series ?? [], [data]);
  const savingsState = data?.measurement?.savings_state;
  const noMeasuredSavings = savingsState === 'no_measured_savings';
  const noData = savingsState === 'no_data';

  // Sin ámbito no hay panel que mostrar (tras TODOS los hooks — orden estable).
  if (!tenantId && !drCode) {
    return null;
  }

  return (
    <section
      data-testid="token-savings-panel"
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 space-y-4"
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{t('policy.tokenSavings')}</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <select
            aria-label={t('policy.savingsWindow')}
            value={window}
            onChange={(e) => setWindow(e.target.value)}
          >
            <option value="24h">{t('policy.window24h')}</option>
            <option value="7d">{t('policy.window7d')}</option>
            <option value="30d">{t('policy.window30d')}</option>
          </select>
          <select
            aria-label={t('policy.savingsUnit')}
            value={unit}
            onChange={(e) => setUnit(e.target.value as 'cost' | 'tokens')}
          >
            <option value="cost">{t('policy.savingsCostOption')}</option>
            <option value="tokens">{t('policy.savingsTokensOption')}</option>
          </select>
          <select
            aria-label={t('policy.savingsGroupBy')}
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
          >
            {groupOptions.map((option) => (
              <option key={option} value={option}>
                {option === 'tenant'
                  ? t('policy.savingsGroupTenant')
                  : option === 'agent'
                    ? t('policy.savingsGroupAgent')
                    : option === 'hive'
                      ? t('policy.savingsGroupHive')
                      : option === 'model'
                        ? t('policy.savingsGroupModel')
                        : t('policy.savingsGroupCostCenter')}
              </option>
            ))}
          </select>
        </div>
      </header>

      {isLoading && <p className="text-sm text-gray-500">{t('policy.loading')}</p>}
      {isError && (
        <p className="text-sm text-red-600 dark:text-red-400" data-testid="token-savings-error">
          {t('policy.savingsLoadError')}
        </p>
      )}

      {data && (
        <>
          {(noMeasuredSavings || noData) && (
            <p
              data-testid="token-savings-not-measured"
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
            >
              {noData ? t('policy.savingsNoData') : t('policy.savingsNotMeasured')}
            </p>
          )}
          {savingsState === 'measured' && data.measurement?.signals?.length ? (
            <p className="text-xs text-gray-500" data-testid="token-savings-signals">
              {t('policy.savingsSignals', {
                signals: data.measurement.signals.join(' · '),
              })}
            </p>
          ) : null}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-xs text-gray-500">
                {unit === 'cost'
                  ? t('policy.savingsEffectiveCost')
                  : t('policy.savingsTokensKpi')}
              </p>
              <p className="text-2xl font-semibold" data-testid="kpi-savings">
                {savingsKpiValue(data.measurement, data.kpis, unit)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('policy.savingsCacheHit')}</p>
              <p className="text-2xl font-semibold">
                {formatPct(data.kpis.cache_hit_rate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">
                {t('policy.savingsCachedTokens')}
              </p>
              <p className="text-2xl font-semibold" data-testid="kpi-cached">
                {formatCachedTokens(
                  data.kpis.cached_tokens,
                  t('policy.savingsNotAvailable'),
                )}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">{t('policy.savingsCoverage')}</p>
              <p className="text-2xl font-semibold">
                {formatPct(data.kpis.coverage_pct)}
              </p>
            </div>
          </div>

          <div className="h-64" data-testid="token-savings-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="produced"
                  name={t('policy.savingsProduced')}
                  dot={false}
                  stroke="#8884d8"
                />
                <Line
                  type="monotone"
                  dataKey="sent"
                  name={t('policy.savingsSent')}
                  dot={false}
                  stroke="#82ca9d"
                />
                <Line
                  type="monotone"
                  dataKey="billable_equivalent"
                  name={t('policy.savingsBillable')}
                  dot={false}
                  stroke="#ffc658"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th>{t('policy.savingsGroupBy')}</th>
                <th>{t('policy.savingsCalls')}</th>
                <th>{t('policy.savingsTokensSaved')}</th>
                <th>{t('policy.savingsCost')}</th>
              </tr>
            </thead>
            <tbody>
              {data.breakdown.slice(0, 10).map((row) => (
                <tr key={row.key}>
                  <td>{row.key}</td>
                  <td>{row.calls}</td>
                  <td>{row.tokens_saved_s2b.toLocaleString()}</td>
                  <td>{formatPct(row.savings_cost_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

export default TokenSavingsPanel;
